import { useOrganization } from '@/contexts/OrganizationContext';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

export function OrganizationSelector() {
  const { currentOrganization, organizations, setCurrentOrganization, isLoading } = useOrganization();
  const [open, setOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="px-3 py-2">
        <div className="h-10 bg-muted animate-pulse rounded-md"></div>
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="px-3 py-2">
        <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50 text-muted-foreground">
          <Building2 className="h-4 w-4" />
          <span className="text-sm">Sem organização</span>
        </div>
      </div>
    );
  }

  if (organizations.length === 1 && currentOrganization) {
    return (
      <div className="px-3 py-2 mb-2">
        <div className="flex items-start gap-3 p-2 rounded-md bg-primary/5 border border-primary/20">
          <Building2 className="h-5 w-5 text-primary mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{currentOrganization.name}</p>
            {currentOrganization.planName && (
              <Badge variant="secondary" className="mt-1 text-xs">
                {currentOrganization.planName}
              </Badge>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 mb-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Building2 className="h-4 w-4 shrink-0" />
              <span className="truncate text-sm">
                {currentOrganization?.name || 'Selecione...'}
              </span>
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar organização..." />
            <CommandEmpty>Nenhuma organização encontrada.</CommandEmpty>
            <CommandGroup>
              {organizations.map((org) => (
                <CommandItem
                  key={org.id}
                  value={org.name}
                  onSelect={() => {
                    setCurrentOrganization(org);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      currentOrganization?.id === org.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{org.name}</p>
                    {org.planName && (
                      <p className="text-xs text-muted-foreground truncate">
                        {org.planName}
                      </p>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
